/**
 * @file        src/pages/flight/AmendmentMarkup.jsx
 * @description Flight Amendment Markup (CRUD) over flight_amendment_markups
 *              (admin migration 078): our own flat, per-passenger markup on a
 *              cancellation, void, full refund, reschedule or post-booking
 *              add-on. One component for both screens — CorporateAmendmentMarkup
 *              renders it fixed to audience=b2b (§9d).
 * @author      Kinetika Labs
 * @created     2026-10-10
 * @changelog
 *   - 2026-10-10 Claude — Initial.
 */
import SimpleLookupPage from '../package/SimpleLookupPage';
import { domIntField } from '../../lib/domInt';

export const FLIGHT_AMENDMENT_TYPES = [
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'void', label: 'Void' },
  { value: 'full_refund', label: 'Full Refund' },
  { value: 'reschedule', label: 'Reschedule' },
  { value: 'addon', label: 'Add-on (seat / meal / baggage)' },
];

const ROLE = [
  { value: 'b2c', label: 'B2C (retail)' },
  { value: 'b2b', label: 'B2B / Corporate' },
];

export default function AmendmentMarkup({
  title = 'Amendment Markup',
  subtitle = 'B2C Flight · our markup on cancellation, void, refund, reschedule and add-ons',
  audience = 'b2c',
} = {}) {
  return (
    <SimpleLookupPage
      title={title}
      subtitle={subtitle}
      endpoint="/flight-amendment-markups"
      queryRoot={`flight-amendment-markups-${audience}`}
      addLabel="Add Amendment Markup"
      entityLabel="Amendment markup"
      listParams={{ audience }}
      notice={<span>A <b>flat amount per person</b> (every passenger, infants included), charged on top of the airline&apos;s own figure and <b>never shown to the customer as a separate line</b>. On a <b>cancellation, void or full refund</b> it is added to the cancellation charge and taken off the refund (a refund never goes below ₹0). On a <b>reschedule</b> it is added to the base-fare difference. On an <b>add-on</b> it is added to the add-on price, once per passenger who buys one. Of the active rules for the booking&apos;s brand, buyer type and amendment, the <b>most specific</b> applies (a Domestic- or International-only rule beats one for both); between equally specific rules the <b>most recently added</b> wins.</span>}
      fields={[
        { key: 'brand', label: 'Brand', type: 'brand', required: true },
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'amendment_type', label: 'Amendment', type: 'select', required: true, options: FLIGHT_AMENDMENT_TYPES },
        { key: 'markup_value', label: 'Markup per Person (₹)', type: 'number', required: true, min: 0, step: '0.01' },
        // Domestic / International trips (blank = both).
        domIntField(),
        // Fixed by the screen, never chosen in the modal (§9d).
        { key: 'applicable_role', label: 'Applies To', type: 'select', options: ROLE, form: false, default: audience === 'b2b' ? 'b2b' : 'b2c' },
      ]}
    />
  );
}
